import React, { useEffect, useMemo } from "react";

import { Box, Typography } from "@material-ui/core";
import { Add, Remove } from "@material-ui/icons";
import { ReactComponent as DeleteIconSvg } from "@remar/shared/dist/assets/icons/icon-trash-filled.svg";
import AddButton from "@remar/shared/dist/ui/Buttons/AddButton";
import IconRoundButton from "@remar/shared/dist/ui/Buttons/IconRoundButton";
import { StyledCheckbox } from "@remar/shared/dist/ui/Buttons/styles";
import get from "lodash/get";
import { useDispatch, useSelector } from "react-redux";

import { _emit } from "store/features/notifications/notifications.slice";

import theme from "theme/default";

import CommonQuestionFields from "./CommonQuestionFields";

import {
	CheckBoxWrapper,
	InputStyles,
	MatrixGroupLabel,
	MatrixTableGroup,
	CustomInput as StyledInput,
	useStyles
} from "./styles";

import { useTestOnlyQuestionStyles } from "../../CreateLesson/TestOnlyQuestions/testOnlyQuestions.styles";

const maxAnswerOptslLength = 3;

export const MatrixMultipleChoiceQuestion = ({
	addOneMoreQuestionAnswerOption,
	createQuestionGroup,
	createSingleMatrixQuestionAnswerOptions,
	getFullState,
	reduceAnswerOptsLength,
	removeQuestionAnswerOptionItem,
	removeQuestionAttachment,
	removeQuestionRationalVideo,
	removeQuestionGroup,
	setStateValue,
	updateMatrixQuestionGroup,
	uploadQuestionMaterial,
	uploadRationalVideo,
	validateForm,
	statePath = "",
	templatePath = "answerOptions",
	formName = "addNewQuestionForm"
}) => {
	const classes = { ...useTestOnlyQuestionStyles(), ...useStyles() };
	const fullStatePath = statePath ? `inputs.${statePath}` : "inputs";
	const questionData = get(useSelector(getFullState), `${formName}.${fullStatePath}`);
	const dispatch = useDispatch();
	const lesson = get(useSelector(getFullState), "lesson");
	const defaultCustomInputOptions = { _emit, dispatch, setStateValue, validateForm };

	const {
		text,
		groups,
		answerOptions,
		tableLabel,
		description,
		attachments,
		rationaleVideo,
		endOfQuestionSummary,
		mainImageKey,
		_expanded
	} = questionData;

	useEffect(() => {
		if (answerOptions.length === 0) {
			dispatch(
				createSingleMatrixQuestionAnswerOptions({
					formName,
					inputsPath: `${fullStatePath}.answerOptions`,
					templatePath,
					setMaxLengthValidation: true
				})
			);
		}
	}, [answerOptions.length]);

	useEffect(() => {
		if (!groups.length) {
			dispatch(createQuestionGroup({ setMaxLengthValidation: true, start: true, statePath, formName }));
		}
	}, [groups.length]);

	const nonDeletedAnswerOptsLength = useMemo(() => answerOptions.filter(x => !x.deleted.value).length, [answerOptions]);
	const nonDeletedGroupsLength = useMemo(() => groups.filter(x => !x.deleted.value).length, [groups]);

	useEffect(() => {
		if (nonDeletedGroupsLength > maxAnswerOptslLength) {
			dispatch(reduceAnswerOptsLength({ inputsPath: `${fullStatePath}.groups`, length: 3 }));
		}
	}, [nonDeletedGroupsLength]);

	return (
		<CommonQuestionFields
			description={description}
			attachments={attachments}
			rationaleVideo={rationaleVideo}
			lessonId={lesson?.id}
			endOfQuestionSummary={endOfQuestionSummary}
			mainImageKey={mainImageKey}
			setStateValue={setStateValue}
			expanded={_expanded?.value}
			validateForm={validateForm}
			removeQuestionAttachment={removeQuestionAttachment}
			removeQuestionRationalVideo={removeQuestionRationalVideo}
			uploadQuestionMaterial={uploadQuestionMaterial}
			uploadRationalVideo={uploadRationalVideo}
		>
			<Typography className={classes.addOptionHeader}>SENTENCE</Typography>
			<StyledInput mr={2} theme={theme} options={{ ...defaultCustomInputOptions, inputData: text }} />
			<Typography className={classes.addOptionHeader}>Table View</Typography>

			<Box display="flex" flexDirection="row" width="100%">
				<Box display="flex" flexDirection="row" alignItems="center" width="calc(100% - 50px)">
					<MatrixGroupLabel valid={!tableLabel.error}>
						<StyledInput
							InputProps={{
								style: {
									...InputStyles
								}
							}}
							noMargin
							theme={theme}
							options={{ ...defaultCustomInputOptions, inputData: { ...tableLabel, label: "" } }}
						/>
					</MatrixGroupLabel>

					<Box display="flex" flexDirection="row" width="40%">
						{groups.map(
							({ text, deleted }, groupIndex) =>
								!deleted?.value && (
									<MatrixTableGroup key={groupIndex}>
										<StyledInput
											theme={theme}
											InputProps={{
												style: {
													...InputStyles
												}
											}}
											noMargin
											options={{
												...defaultCustomInputOptions,
												inputData: { ...text, label: "" }
											}}
										/>
									</MatrixTableGroup>
								)
						)}
					</Box>
				</Box>

				{nonDeletedGroupsLength === 2 && (
					<IconRoundButton
						color={"primary"}
						variant={"contained"}
						style={{ width: "20px", height: "20px", alignSelf: "center", marginLeft: "10px" }}
						Icon={<Add style={{ width: "15px", height: "15px" }} fill={"#fff"} />}
						onClick={() => {
							dispatch(
								createQuestionGroup({
									setMaxLengthValidation: true,
									statePath,
									formName
								})
							);
						}}
					/>
				)}

				{nonDeletedGroupsLength === 3 && (
					<IconRoundButton
						color={"primary"}
						variant={"contained"}
						style={{ width: "20px", height: "20px", alignSelf: "center", marginLeft: "10px", background: "red" }}
						Icon={<Remove style={{ width: "15px", height: "15px" }} fill={"red"} />}
						onClick={() => {
							const index = groups.map(x => x.deleted.value).lastIndexOf(false);
							if (index > -1) {
								dispatch(
									removeQuestionGroup({
										statePath,
										groupIndex: index
									})
								);
							}
						}}
					/>
				)}
			</Box>

			{answerOptions.map(
				({ id, text, deleted }, aIndex) =>
					!deleted?.value && (
						<Box key={`ans_${id}`} display="flex" flexDirection="row" width="100%">
							<Box key={aIndex} display="flex" flexDirection="row" width="calc(100% - 50px)">
								<MatrixGroupLabel>
									<StyledInput
										noMargin
										InputProps={{
											style: {
												...InputStyles,
												backgroundColor: "#2a2e37"
											}
										}}
										theme={theme}
										options={{
											...defaultCustomInputOptions,
											inputData: text
										}}
									/>
								</MatrixGroupLabel>
								<Box display="flex" flexDirection="row" width="40%">
									<CheckBoxWrapper>
										{groups.map(
											({ selectedAnswerOptions, deleted }, gIndex) =>
												!deleted?.value && (
													<Box
														justifyContent="center"
														key={gIndex}
														border="solid 1px rgb(151, 151, 151, 0.3)"
														display="flex"
														width={`${100 / nonDeletedGroupsLength}%`}
													>
														<StyledCheckbox
															style={{ margin: "10px" }}
															onChange={e => {
																dispatch(
																	updateMatrixQuestionGroup({
																		key: `${formName}.${fullStatePath}.groups.${gIndex}.selectedAnswerOptions`,
																		value: e.target.checked,
																		optId: id.value
																	})
																);
																dispatch(validateForm({ formStatePath: formName }));
															}}
															checked={selectedAnswerOptions.value.includes(id.value)}
														/>
													</Box>
												)
										)}
									</CheckBoxWrapper>
								</Box>
							</Box>

							{nonDeletedAnswerOptsLength > 2 && (
								<Box
									display="flex"
									alignItems="center"
									justifyContent="center"
									width="50px !important"
									className={classes.addOptionButtonContainer}
								>
									<DeleteIconSvg
										onClick={() => {
											dispatch(
												removeQuestionAnswerOptionItem({
													formName,
													inputsPath: `${fullStatePath}.answerOptions.${aIndex}`,
													removeSelectedOpts: true,
													statePath: fullStatePath,
													id: answerOptions[aIndex].id.value
												})
											);
										}}
									/>
								</Box>
							)}
						</Box>
					)
			)}

			<Box display="flex" flexDirection="row">
				{nonDeletedAnswerOptsLength < 6 && (
					<AddButton
						onClick={() => {
							dispatch(
								addOneMoreQuestionAnswerOption({
									formName,
									inputsPath: `${fullStatePath}.answerOptions`,
									templatePath,
									setMaxLengthValidation: true
								})
							);
						}}
						label={"Add row"}
					/>
				)}
			</Box>
		</CommonQuestionFields>
	);
};
